import { useQuery } from '@tanstack/react-query';
import api, { type SpendByArche } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Fetches the signed-in user's net credit spend grouped by arche/plugin for
 * the Credits page breakdown. Names + icons are pre-resolved server-side, so
 * this is a single request with no follow-up metadata lookups.
 *
 * React Query (unlike the older `useCredits` useState hook) so the 5-minute
 * `staleTime` dedupes refetches while the user toggles around the page.
 *
 * `refetchOnWindowFocus` is explicitly disabled. The app-wide default in
 * `query-client.ts` is `'always'`, which ignores `staleTime` — every tab focus
 * would re-run an aggregation that scans all of the user's credit_transactions
 * rows (deliberately unindexed; see the migration rationale). Spend history is
 * not the kind of data that changes while the user is on another tab, and the
 * page's Refresh button already invalidates the `credits` namespace when they
 * do want it re-read.
 */
export function useSpendByArche(): {
  arches: SpendByArche[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.credits.spendByArche(),
    queryFn: () => api.getCreditSpendByArche(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    arches: data?.arches ?? [],
    isLoading,
    isError,
  };
}
